import React, { useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  Typography,
  Button,
  Flex,
  Space,
  Divider,
  DatePicker,
  Tooltip,
  Skeleton,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

import { CheckCircleOutlined } from '@ant-design/icons';

import ChannelDropDown from './ChannelDropDown';
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
import { useDispatch } from 'react-redux';
import {
  clearSelectedCountryList,
  clearWhatsappBusinessNumbersList,
  disconnectWhatsappChannel,
  fetchWhatsappBusinessInsights,
  setCountryList,
  setDateRangeFilter,
  setWhatsappBusinessNumbersList,
  fetchCountryList,
} from '../../../../reduxToolkit/channelSlice';
import { useSelector } from 'react-redux';
import { ICONS } from '../../../../assets/icons';
import Download from '../../../../assets/customSVG/campaigns/Download';
import { daily_messaging_limit } from '../../../../modules/channels/constant/phoneNumbers';

const WhatsappConnectionHeader = () => {
  const dispatch = useDispatch();
  const { currentBrand } = useSelector((state) => state.app);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [syncDisabled, setSyncDisabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const brandId = currentBrand?.brand_id;
  const {
    businessProfile,
    whatsappBusinessNumbersListLoading,
    phoneNumbersList,
    countryList,
    countryListLoading,
    getBusinessProfileLoading,
  } = useSelector((state) => state.Channel);

  console.log('countryList', countryList);

  const userInteracted = () => {
    hasUserInteracted == false && setHasUserInteracted(true);
  };
  // Helper to get/set query params
  const getDateRangeFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const start = params.get('startDate');
    const end = params.get('endDate');
    if (start && end) {
      // Always return new dayjs objects
      return [dayjs(Number(start)), dayjs(Number(end))];
    }
    // Default to last 7 days if not set
    const today = dayjs().endOf('day');
    const lastWeek = dayjs().subtract(6, 'day').startOf('day');
    return [dayjs(lastWeek), dayjs(today)];
  };

  const setDateRangeToQuery = (start, end) => {
    const params = new URLSearchParams(window.location.search);
    if (start && end) {
      params.set('startDate', start.startOf('day').valueOf());
      params.set('endDate', end.endOf('day').valueOf());
    } else {
      params.delete('startDate');
      params.delete('endDate');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  // Initialize dateRange state
  const [dateRange, setDateRange] = useState(() => getDateRangeFromQuery());

  // On mount, always sync dateRangeFilter in Redux with initial date range
  useEffect(() => {
    // Compute initial range (in seconds)
    const [start, end] = getDateRangeFromQuery();
    dispatch(
      setDateRangeFilter([
        start.startOf('day').valueOf() / 1000,
        end.endOf('day').valueOf() / 1000,
      ]),
    );
  }, []);

  // If query params are missing, set them on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const start = params.get('startDate');
    const end = params.get('endDate');
    if (!start || !end) {
      const today = dayjs().endOf('day');
      const lastWeek = dayjs().subtract(6, 'day').startOf('day');
      setDateRangeToQuery(lastWeek, today);
    }
  }, []);

  // Sync state with query params on mount
  useEffect(() => {
    setDateRange(getDateRangeFromQuery());
  }, []);

  // Check localStorage for last sync time on mount
  useEffect(() => {
    const lastSync = localStorage.getItem('whatsapp_sync_last_time');
    if (lastSync) {
      const diff = 60 - Math.floor((Date.now() - Number(lastSync)) / 1000);
      if (diff > 0) {
        setSyncDisabled(true);
        setSecondsLeft(diff);
        const interval = setInterval(() => {
          const newDiff =
            60 - Math.floor((Date.now() - Number(lastSync)) / 1000);
          setSecondsLeft(newDiff > 0 ? newDiff : 0);
          if (newDiff <= 0) {
            setSyncDisabled(false);
            clearInterval(interval);
          }
        }, 1000);
        return () => clearInterval(interval);
      }
    }
  }, []);

  useEffect(() => {
    dispatch(fetchCountryList());
  }, []);

  // Handler for RangePicker
  const handleRangeChange = (dates) => {
    userInteracted();
    if (dates && dates.length === 2) {
      setDateRange([dayjs(dates[0]), dayjs(dates[1])]);
      setDateRangeToQuery(dayjs(dates[0]), dayjs(dates[1]));
      dispatch(
        setDateRangeFilter([
          dayjs(dates[0]).startOf('day').valueOf() / 1000,
          dayjs(dates[1]).endOf('day').valueOf() / 1000,
        ]),
      );
    } else {
      // Default to last 7 days if cleared
      const today = dayjs().endOf('day');
      const lastWeek = dayjs().subtract(6, 'day').startOf('day');
      setDateRange([dayjs(lastWeek), dayjs(today)]);
      setDateRangeToQuery(dayjs(lastWeek), dayjs(today));
      dispatch(
        setDateRangeFilter([
          dayjs(lastWeek).startOf('day').valueOf() / 1000,
          dayjs(today).endOf('day').valueOf() / 1000,
        ]),
      );
    }
  };

  // Fetch insights when relevant state changes
  useEffect(() => {
    if (hasUserInteracted && dateRange && dateRange.length === 2) {
      dispatch(
        fetchWhatsappBusinessInsights({
          brand_id: currentBrand?.brand_id,
          start_date: dayjs(dateRange[0]).startOf('day').valueOf() / 1000,
          end_date: dayjs(dateRange[1]).endOf('day').valueOf() / 1000,
          phone_numbers: phoneNumbersList
            .filter((item) => item.checked)
            .map((item) => item.value),
          countries: countryList
            .filter((item) => item.checked)
            .map((item) => item.value),
        }),
      );
    }
  }, [dateRange, phoneNumbersList, countryList, currentBrand, dispatch]);

  const handlePhoneNumberChange = (id) => {
    userInteracted();
    dispatch(setWhatsappBusinessNumbersList(id));
  };

  const handleCountryChange = (id) => {
    userInteracted();
    dispatch(setCountryList(id));
  };

  const clearSelectedCountry = () => {
    userInteracted();
    dispatch(clearSelectedCountryList());
  };

  const clearSelectedPhoneNumbers = () => {
    userInteracted();
    dispatch(clearWhatsappBusinessNumbersList());
  };

  // Handler for Sync button
  const handleSyncClick = () => {
    if (dateRange && dateRange.length === 2) {
      dispatch(
        fetchWhatsappBusinessInsights({
          brand_id: currentBrand?.brand_id,
          start_date: dayjs(dateRange[0]).startOf('day').valueOf() / 1000,
          end_date: dayjs(dateRange[1]).endOf('day').valueOf() / 1000,
          phone_numbers: phoneNumbersList
            .filter((item) => item.checked)
            .map((item) => item.value),
          countries: countryList
            .filter((item) => item.checked)
            .map((item) => item.value),
        }),
      );
      // Set cooldown
      localStorage.setItem('whatsapp_sync_last_time', Date.now().toString());
      setSyncDisabled(true);
      setSecondsLeft(60);
      const interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            setSyncDisabled(false);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // function to capture the full scrool screen and download it
  const handleDownload = () => {
    const container = document.getElementById('selected_screenshot_element');
    if (!container) {
      console.error('Could not find screenshot container');
      return;
    }

    // Create a temporary clone of the date range picker
    const originalPicker = document.querySelector('.ant-picker');
    if (originalPicker) {
      // Hide the original picker
      originalPicker.style.display = 'none';

      // Create a new simple date range display
      const dateRangeClone = document.createElement('div');
      dateRangeClone.className = 'whatsapp-date-range-clone';
      dateRangeClone.style.position = 'relative';
      dateRangeClone.style.padding = '12px 16px';
      dateRangeClone.style.backgroundColor = '#ffffff';
      dateRangeClone.style.borderRadius = '8px';
      dateRangeClone.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
      dateRangeClone.style.zIndex = '9999';
      dateRangeClone.style.transform = 'translateZ(0)';

      // Format and display the date range
      const startDate = dateRange[0]?.format('YYYY-MM-DD') || 'Start Date';
      const endDate = dateRange[1]?.format('YYYY-MM-DD') || 'End Date';
      dateRangeClone.innerHTML = `
                  <div style="display: flex;align-items: center;gap: 8px;">
                    <span style="font-size: 14px; color: #333;">
                      ${startDate} -> ${endDate}
                    </span>
                  </div>`;

      // Add the clone to the append_range_picker container
      const appendRangePicker = document.getElementById('append_range_picker');
      if (appendRangePicker) {
        // Remove any existing clone first
        const existingClone = appendRangePicker.querySelector(
          '.whatsapp-date-range-clone',
        );
        if (existingClone) {
          existingClone.remove();
        }
        appendRangePicker.appendChild(dateRangeClone);
      }

      // Wait for styles to apply and take screenshot
      setTimeout(() => {
        const elementsToHide = document.querySelectorAll('.no-screenshot');
        elementsToHide.forEach((el) => (el.style.visibility = 'hidden'));

        html2canvas(container, {
          useCORS: true,
          scale: 2,
          logging: true,
          scrollX: 0,
          scrollY: 0,
          width: container.scrollWidth,
          height: container.scrollHeight,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          allowTaint: true,
          backgroundColor: '#ffffff',
          onclone: (doc) => {
            // Add the date range clone to the cloned document
            const appendRangePicker = doc.getElementById('append_range_picker');
            if (appendRangePicker) {
              // Remove any existing clone first
              const existingClone = appendRangePicker.querySelector(
                '.whatsapp-date-range-clone',
              );
              if (existingClone) {
                existingClone.remove();
              }

              const cloneInDoc = doc.createElement('div');
              cloneInDoc.className = 'whatsapp-date-range-clone';
              cloneInDoc.style.position = 'relative';
              cloneInDoc.style.padding = '12px 16px';
              cloneInDoc.style.backgroundColor = '#ffffff';
              cloneInDoc.style.borderRadius = '8px';
              cloneInDoc.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              cloneInDoc.style.zIndex = '9999';
              cloneInDoc.innerHTML = dateRangeClone.innerHTML;
              appendRangePicker.appendChild(cloneInDoc);
            }
          },
        }).then((canvas) => {
          // Restore visibility
          elementsToHide.forEach((el) => (el.style.visibility = 'visible'));

          // Restore original picker
          if (originalPicker) {
            originalPicker.style.display = '';
          }

          // Remove the clone from both the original and cloned document
          if (dateRangeClone) {
            dateRangeClone.remove();
          }

          // Download the screenshot
          const link = document.createElement('a');
          link.download = 'whatsapp-insights.png';
          link.href = canvas.toDataURL();
          link.click();
        });
      }, 100);
    }
  };

  return (
    <div className="bg-white rounded-xl px-4 py-3 shadow-sm">
      <Flex justify="space-between" align="center" className="mb-4">
        <Flex align="center" gap="middle">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            alt="whatsapp"
            className="w-12 h-12 rounded-full"
          />
          {getBusinessProfileLoading ? (
            <div style={{ minWidth: 220 }}>
              <Skeleton
                active
                paragraph={{ rows: 1, width: '60%' }}
                title={{ width: '80%' }}
              />
              <Skeleton.Input
                active
                size="small"
                style={{ width: 180, marginTop: 8 }}
              />
            </div>
          ) : (
            <Space direction="vertical" size={0}>
              <Flex align="center" gap="small">
                <Title level={5} style={{ margin: 0 }}>
                  {businessProfile?.whatsapp_business_account_name}
                </Title>
                {businessProfile?.is_whatsapp_business_account_verified && (
                  <div className="text-blue-500 text-xl">
                    <CheckCircleOutlined />
                  </div>
                )}
              </Flex>
              <Text type="secondary">
                WhatsApp Account ID:{' '}
                {businessProfile?.whatsapp_business_account_id}
              </Text>
              <Text type="secondary">
                Messaging Limit:{' '}
                {daily_messaging_limit[businessProfile?.messaging_limit]}{' '}
                {daily_messaging_limit[businessProfile?.messaging_limit] !==
                'Unavailable'
                  ? '/ Day'
                  : ''}
              </Text>
            </Space>
          )}
        </Flex>
        <Space>
          <Tooltip
            title={
              currentBrand?.brand_mode === 'test'
                ? "You can't sync in test mode"
                : syncDisabled
                ? `try after ${secondsLeft} seconds`
                : 'Sync'
            }
          >
            <Button
              className="no-screenshot"
              onClick={handleSyncClick}
              icon={
                <img
                  src={ICONS.syncRefreshTemplate}
                  alt="refresh icon"
                  className="size-4"
                />
              }
              disabled={syncDisabled || currentBrand?.brand_mode === 'test'}
            >
              Sync
            </Button>
          </Tooltip>
          <Tooltip
            title={
              currentBrand?.brand_mode === 'test'
                ? "You can't disconnect in test mode"
                : 'Disconnect'
            }
          >
            <Button
              className="no-screenshot"
              onClick={() => dispatch(disconnectWhatsappChannel(brandId))}
              icon={
                <img
                  src={ICONS.disConnect}
                  alt="disconnect icon"
                  className="size-4"
                />
              }
              disabled={currentBrand?.brand_mode === 'test'}
              danger
            >
              Disconnect
            </Button>
          </Tooltip>
        </Space>
      </Flex>

      <Divider className="my-3" />

      <Flex gap="middle" justify="space-between" wrap className="mb-4">
        <Space>
          <ChannelDropDown
            dropDownLabel="Phone number"
            dropDownList={phoneNumbersList.map((item) => {
              const phone_number = item.name.split(' ');
              const beautify_phone_number = `${phone_number[1].slice(0, 3)} 
          ${phone_number[1].slice(3, 6)} ${phone_number[1].slice(6)}`;
              return {
                ...item,
                name: `(+${phone_number[0]}) ${beautify_phone_number}`,
              };
            })}
            onchange={handlePhoneNumberChange}
            onClear={clearSelectedPhoneNumbers}
            listLoading={whatsappBusinessNumbersListLoading}
          />
          <ChannelDropDown
            dropDownLabel="Country"
            dropDownList={countryList}
            onchange={handleCountryChange}
            onClear={clearSelectedCountry}
            listLoading={countryListLoading}
          />
        </Space>

        <Space>
          <div id="append_range_picker">
            <RangePicker
              className="w-64"
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), 'day')
              }
              getPopupContainer={() =>
                document.getElementById('selected_screenshot_element')
              }
              value={dateRange}
              onChange={handleRangeChange}
              format="YYYY-MM-DD"
            />
          </div>
          <Button
            icon={<Download className={'size-4'} />}
            onClick={handleDownload}
          />
        </Space>
      </Flex>
    </div>
  );
};

//     const link = document.createElement("a");
export default WhatsappConnectionHeader;
